import { useEffect, useMemo, useRef, useState } from "react";
import { X, Maximize2, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen image lightbox with optional gallery navigation.
 *
 * Two supported usages:
 *   1) Single image mode:  <PortfolioLightbox open src alt onClose />
 *   2) Gallery mode:       <PortfolioLightbox open items={[{src,alt}]} index onIndexChange onClose />
 *
 * Gallery mode enables:
 *   - Keyboard: ← / → to navigate, Esc to close
 *   - Touch: swipe left / right to navigate
 *   - Buttons: on-screen prev / next arrows
 *
 * Image is rendered to fit within the viewport (no zoom, no panning).
 */
export default function PortfolioLightbox({
  open,
  src,
  alt,
  items,
  index = 0,
  onIndexChange,
  onClose,
}) {
  // Normalize input: gallery items take precedence, otherwise fall back to single src
  const gallery = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) return items;
    if (src) return [{ src, alt }];
    return [];
  }, [items, src, alt]);

  const total = gallery.length;
  const [localIndex, setLocalIndex] = useState(index);

  useEffect(() => {
    setLocalIndex(Math.max(0, Math.min(index, total - 1)));
  }, [index, total, open]);

  const active = gallery[localIndex];
  const canNavigate = total > 1;

  const go = (delta) => {
    if (!canNavigate) return;
    const next = ((localIndex + delta) % total + total) % total;
    setLocalIndex(next);
    onIndexChange?.(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canNavigate, localIndex, total]);

  // Touch swipe support
  const touchRef = useRef({ x: 0, y: 0, active: false });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current.active = false;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  if (!open || !active) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-6 bg-black/85 backdrop-blur-sm cursor-zoom-out"
      data-testid="portfolio-lightbox"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="portfolio-lightbox-close"
        className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur transition-colors z-10"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>

      {canNavigate && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            data-testid="portfolio-lightbox-prev"
            aria-label="Previous"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur transition-colors z-10"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            data-testid="portfolio-lightbox-next"
            aria-label="Next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur transition-colors z-10"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <img
        key={active.src}
        src={active.src}
        alt={active.alt || ""}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        data-testid="portfolio-lightbox-image"
        className="block max-w-[95vw] max-h-[90vh] w-auto h-auto object-contain cursor-default rounded-lg shadow-2xl select-none"
      />

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full pointer-events-none">
        <Maximize2 className="h-3.5 w-3.5" />
        {canNavigate ? (
          <span>
            {String(localIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · swipe or ← →
          </span>
        ) : (
          <span>Fit to screen · click to close</span>
        )}
      </div>
    </div>
  );
}
